import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Droplets,
  HeartHandshake,
  HandHeart,
  Network,
  ArrowRight,
  Users,
  Gift,
  Sparkles,
  Search,
} from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { api } from '../services/api';
import { cn } from '../lib/utils';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/missoes'],
      ['Área Administrativa', '/admin'],
    ],
  },
];

const HERO =
  'https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const CATEGORIES = ['Todas', 'Recuperação', 'Assistência Social', 'Idosos', 'Saúde', 'Comunidade', 'Habitação'];

function HeroSection() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO} alt="Missões da Comunidade Águas Vivas" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/85 via-primary-dark/70 to-primary-dark/90" />
      </div>

      <div className="app-container relative py-16 md:py-24">
        <div className="max-w-2xl">
          <Badge tone="accent" className="mb-4">
            <HandHeart size={12} /> Missões Sociais
          </Badge>
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white md:text-5xl">
            Nossas Missões
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
            Cada rede apoia uma missão, e algumas precisam da sua doação. Juntos, levamos amor,
            esperança e transformação à nossa cidade.
          </p>
        </div>
      </div>
    </section>
  );
}

function StatsBar({ missions }) {
  const withNetwork = missions.filter((m) => m.networkName).length;
  const needDonation = missions.filter((m) => m.needsDonation).length;

  return (
    <div className="app-container -mt-8 relative z-10">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-soft transition-transform duration-200 hover:-translate-y-0.5">
          <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-primary-light text-primary">
            <HandHeart size={18} />
          </span>
          <p className="mt-3 font-heading text-3xl font-extrabold text-ink">{missions.length}</p>
          <p className="text-xs font-medium text-ink-soft">Missões ativas</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-soft transition-transform duration-200 hover:-translate-y-0.5">
          <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-accent-light text-accent-dark">
            <Network size={18} />
          </span>
          <p className="mt-3 font-heading text-3xl font-extrabold text-ink">{withNetwork}</p>
          <p className="text-xs font-medium text-ink-soft">Apoiadas por redes</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-soft transition-transform duration-200 hover:-translate-y-0.5">
          <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <Gift size={18} />
          </span>
          <p className="mt-3 font-heading text-3xl font-extrabold text-ink">{needDonation}</p>
          <p className="text-xs font-medium text-ink-soft">Precisam de doação</p>
        </div>
      </div>
    </div>
  );
}

function MissionCard({ mission }) {
  return (
    <article
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
      data-testid={`mission-card-${mission.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={mission.image}
          alt={mission.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <Badge tone="accent" className="absolute left-3 top-3">
          {mission.category}
        </Badge>
        {mission.needsDonation && (
          <Badge tone="danger" className="absolute right-3 top-3">
            <Gift size={11} /> Precisa de doação
          </Badge>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-lg font-bold text-ink">{mission.name}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-soft">{mission.description}</p>

        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
          {mission.networkName ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-primary">
              <Network size={14} /> {mission.networkName}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-ink-faint">
              <Users size={14} /> Sem rede de apoio
            </span>
          )}
          <Link
            to="/contato"
            data-testid={`mission-donate-${mission.id}`}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-heading text-xs font-bold transition-all duration-200 hover:-translate-y-0.5',
              mission.needsDonation
                ? 'bg-accent text-white hover:bg-accent-dark'
                : 'bg-primary-light text-primary hover:bg-primary hover:text-white'
            )}
          >
            {mission.needsDonation ? 'Doar' : 'Apoiar'} <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Missoes() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('Todas');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.getMissions().then((m) => {
      setMissions(m);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    return missions.filter((m) => {
      const matchCat = category === 'Todas' || m.category === category;
      const matchQuery = !query || m.name.toLowerCase().includes(query.toLowerCase());
      return matchCat && matchQuery;
    });
  }, [missions, category, query]);

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <HeroSection />
      <StatsBar missions={missions} />

      <section className="app-container mt-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-heading text-xl font-extrabold text-ink md:text-2xl">
              Conheça cada missão
            </h2>
            <p className="mt-1 text-sm text-ink-soft">
              Cada rede apoia uma missão. Algumas precisam da sua doação para continuar.
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-1.5 shadow-soft md:max-w-xs">
            <Search size={16} className="ml-2 shrink-0 text-ink-faint" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar missão..."
              className="w-full bg-transparent py-1.5 text-sm text-ink outline-none placeholder:text-ink-faint"
              data-testid="missions-search"
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-2" data-testid="missions-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`missions-cat-${c}`}
              className={cn(
                'shrink-0 rounded-full px-4 py-2 font-heading text-sm font-semibold transition-colors duration-200',
                category === c
                  ? 'bg-primary text-white shadow-soft'
                  : 'border border-slate-200 bg-white text-ink-soft hover:text-primary'
              )}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft">
                <Skeleton className="aspect-[16/10] w-full" />
                <div className="p-5">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="mt-3 h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-4/5" />
                </div>
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <Sparkles size={32} className="mx-auto text-ink-faint" />
              <p className="mt-3 font-heading font-bold text-ink">Nenhuma missão encontrada</p>
              <p className="mt-1 text-sm text-ink-soft">Tente ajustar a busca ou o filtro.</p>
            </div>
          ) : (
            filtered.map((m) => <MissionCard key={m.id} mission={m} />)
          )}
        </div>
      </section>

      <section className="app-container mt-14">
        <div className="relative overflow-hidden rounded-2xl bg-accent-dark p-6 text-white shadow-lift md:p-10">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-primary/20" />
          <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
                <HeartHandshake size={24} />
              </span>
              <h3 className="mt-4 font-heading text-xl font-extrabold md:text-2xl">
                Quer apoiar uma missão?
              </h3>
              <p className="mt-2 max-w-md text-sm text-white/80">
                Sua doação sustenta projetos que transformam vidas. Veja para onde vai cada
                contribuição e faça parte dessa corrente do bem.
              </p>
            </div>
            <Link
              to="/contato"
              data-testid="missions-cta"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-sm font-bold text-accent-dark transition-transform duration-200 hover:-translate-y-0.5"
            >
              <Droplets size={16} /> Quero doar
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
